
import React, { useState, useRef, useEffect } from 'react';
import { Chat, ModalType, InboxConversation } from '../types';
import { InviteIcon, FeedbackIcon, BugIcon, AddChatIcon, DownloadIcon, EditIcon, MenuCloseIcon } from './icons';
import { trackClick } from '../utils/analytics';

interface RightSidebarProps {
    openModal: (type: ModalType) => void;
    chats: Chat[];
    activeChatId: string | null;
    onSelectChat: (id: string) => void;
    onAddNewChat: () => void;
    onRenameChat: (id: string, title: string) => void;
    onDownloadChat: (id: string) => void;
    inboxConversations: InboxConversation[];
    activeConversationId: string | null;
    onSelectConversation: (id: string) => void;
    activeView: string;
    isLoading?: boolean; // Whether a search is currently running
    onClose?: () => void;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({ 
    openModal,
    chats,
    activeChatId,
    onSelectChat,
    onAddNewChat,
    onRenameChat,
    onDownloadChat,
    inboxConversations,
    activeConversationId,
    onSelectConversation,
    activeView,
    isLoading = false,
    onClose
}) => {
    const [editingChatId, setEditingChatId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const [visibleChatsCount, setVisibleChatsCount] = useState(10); // Start with 10 chats visible

    useEffect(() => {
        if (editingChatId && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingChatId]);

    // Reset visible count when chats change (e.g., new chat added)
    useEffect(() => {
        setVisibleChatsCount(10);
    }, [chats.length]);

    // Get visible chats based on pagination
    const visibleChats = chats.slice(0, visibleChatsCount);
    const hasMoreChats = chats.length > visibleChatsCount;

    const handleLoadMore = () => {
        setVisibleChatsCount(prev => prev + 10);
    };

    const handleRename = (chatId: string) => {
        if(editingTitle.trim()){
            onRenameChat(chatId, editingTitle.trim());
        }
        setEditingChatId(null);
        setEditingTitle('');
    };

    const startEditing = (chat: Chat) => {
        setEditingChatId(chat.id);
        setEditingTitle(chat.title);
    }
  
    return (
        <aside className={`w-full lg:w-[244px] flex-shrink-0 flex flex-col ${activeView === 'inbox' ? 'gap-2' : 'gap-4'} lg:rounded-xl p-4 bg-white lg:bg-transparent lg:self-stretch max-h-screen lg:max-h-none`}>
            <div className="flex justify-between items-center flex-shrink-0">
                <div className="flex flex-1 justify-center items-start gap-4">
                    <button onClick={() => { trackClick('sidebar_click_invite'); openModal(ModalType.Invite); }} className="flex flex-col justify-center items-center gap-1">
                        <div className="flex w-12 h-12 p-3 flex-col justify-center items-center gap-2 rounded-xl">
                            <InviteIcon />
                        </div>
                        <div className="self-stretch text-grayscale-1100 text-center font-grotesk text-[10px] leading-none">Invite</div>
                    </button>
                    <button onClick={() => { trackClick('sidebar_click_feedback'); openModal(ModalType.Feedback); }} className="flex flex-col justify-center items-center gap-1">
                        <div className="flex w-12 h-12 p-3 flex-col justify-center items-center gap-2 rounded-xl">
                            <FeedbackIcon />
                        </div>
                        <div className="self-stretch text-grayscale-1100 text-center font-grotesk text-[10px] leading-none">Feedback</div>
                    </button>
                     <button onClick={() => { trackClick('sidebar_click_bug'); openModal(ModalType.Bug); }} className="flex flex-col justify-center items-center gap-1">
                        <div className="flex w-12 h-12 p-3 flex-col justify-center items-center gap-2 rounded-xl">
                            <BugIcon />
                        </div>
                        <div className="self-stretch text-grayscale-1100 text-center font-grotesk text-[10px] leading-none">Bug</div>
                    </button>
                </div>
                {onClose && (
                    <div className="lg:hidden">
                        <button onClick={onClose} className="p-2">
                            <MenuCloseIcon />
                        </button>
                    </div>
                )}
            </div>
            
            {activeView !== 'inbox' && (
                <button 
                    onClick={() => {
                        if (!isLoading) {
                            trackClick('sidebar_click_add_chat');
                            onAddNewChat();
                        }
                    }}
                    disabled={isLoading}
                    className={`flex h-12 py-2 px-4 justify-between items-center rounded-lg flex-shrink-0 transition-all ${
                        isLoading 
                            ? 'cursor-not-allowed opacity-50 bg-gray-50' 
                            : 'hover:bg-gray-100'
                    }`}
                >
                    <div className="flex items-center gap-[10px]">
                        <AddChatIcon />
                        <span className="text-grayscale-1300 font-grotesk text-sm font-bold">Add Chat</span>
                    </div>
                    <div></div>
                </button>
            )}

            <div className={`flex flex-col justify-top items-center overflow-y-auto hide-scrollbar flex-1 min-h-0 lg:min-h-0 ${activeView === 'inbox' ? 'gap-1' : ''}`} style={{ WebkitOverflowScrolling: 'touch' }}>
                {activeView === 'inbox' ? (
                    inboxConversations.map(convo => (
                        <div
                            key={convo.id}
                            className={`flex w-full p-[10px_16px] justify-between items-start rounded-lg transition-all ${
                                isLoading 
                                    ? 'cursor-not-allowed opacity-50 pointer-events-none' 
                                    : 'cursor-pointer hover:bg-gray-100'
                            } ${activeConversationId === convo.id ? 'bg-primary-100/50' : ''}`}
                            onClick={() => {
                                if (!isLoading) {
                                    onSelectConversation(convo.id);
                                }
                            }}
                        >
                            <div className="flex flex-col justify-center items-start gap-2 w-full">
                                <div className="flex flex-col items-start gap-1">
                                    <div className="text-grayscale-1200 font-grotesk text-sm font-bold">{convo.contactName}</div>
                                    <div className="text-grayscale-1000 font-grotesk text-xs">{convo.contactAffiliation}</div>
                                    <div className="text-grayscale-1000 font-grotesk text-[10px] italic font-light">{convo.lastMessageTimestamp}</div>
                                </div>
                                <div className="text-grayscale-1000 font-grotesk text-xs self-stretch overflow-hidden text-ellipsis whitespace-nowrap">
                                    {convo.messagePreview}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <>
                        {visibleChats.map(chat => (
                            <div 
                                key={chat.id} 
                                className={`flex w-full p-[10px_16px] justify-between items-center rounded-lg transition-all ${
                                    isLoading 
                                        ? 'cursor-not-allowed opacity-50 pointer-events-none' 
                                        : 'cursor-pointer hover:bg-gray-100'
                                } ${activeChatId === chat.id ? 'bg-primary-100/50' : ''}`}
                                onClick={() => {
                                    if (!isLoading) {
                                        trackClick('sidebar_click_past_chat', { chat_id: chat.id });
                                        onSelectChat(chat.id);
                                    }
                                }}
                            >
                                <div className="flex flex-col justify-center items-start gap-1">
                                    {editingChatId === chat.id ? (
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            onBlur={() => handleRename(chat.id)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(chat.id); }}
                                            className="bg-transparent text-grayscale-1300 font-grotesk text-sm font-bold focus:outline-none w-full"
                                        />
                                    ) : (
                                        <div className="text-grayscale-1300 font-grotesk text-sm font-bold">{chat.title}</div>
                                    )}
                                    <div className="text-grayscale-1000 font-grotesk text-xs">{chat.date}</div>
                                </div>
                                {activeChatId === chat.id && (
                                    <div className="flex items-center gap-2">
                                        {/* <button onClick={(e) => { e.stopPropagation(); onDownloadChat(chat.id);}}><DownloadIcon /></button> */}
                                        <button 
                                            onClick={(e) => { 
                                                e.stopPropagation(); 
                                                if (!isLoading) {
                                                    startEditing(chat);
                                                }
                                            }}
                                            disabled={isLoading}
                                            className={isLoading ? 'cursor-not-allowed opacity-50' : ''}
                                        >
                                            <EditIcon />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                        {hasMoreChats && (
                            <button
                                onClick={handleLoadMore}
                                disabled={isLoading}
                                className={`w-full py-2 px-4 mt-2 text-center text-sm font-medium rounded-lg transition-colors ${
                                    isLoading 
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                        : 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                                }`}
                            >
                                Load More ({chats.length - visibleChatsCount} remaining)
                            </button>
                        )}
                    </>
                )}
            </div>
        </aside>
    );
};
